"use client"

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { motion } from "framer-motion"
import { Trophy, Sparkles, Flame } from "lucide-react"
import { getCzechDaysText } from "@/lib/utils"

interface StreakMilestoneDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  milestone: number
}

const milestoneMessages: Record<number, { title: string; message: string; emoji: string }> = {
  7: {
    title: "🔥 Týdenní série!",
    message: "Skvělé! Máš 7 dní v řadě. Začínáš si vytvářet návyk sledování své energie.",
    emoji: "🎯",
  },
  30: {
    title: "⭐ Měsíční mistr!",
    message: "Wow! 30 dní v řadě! Jsi neuvěřitelně disciplinovaný. Sledování své vnitřní mapy se stalo součástí tvého života.",
    emoji: "👑",
  },
  100: {
    title: "🏆 Stoletá legenda!",
    message: "Neuvěřitelné! 100 dní v řadě! Jsi pravým mistrem sebepoznání. Tvá vnitřní mapa je nyní bohatá a plná cenných dat.",
    emoji: "💎",
  },
  365: {
    title: "🌟 Roční fenomén!",
    message: "Ohromující! Celý rok bez přerušení! Jsi absolutní legenda. Tvé sebepozorování je na úrovni, které dosahuje jen málokdo.",
    emoji: "🌈",
  },
}

export function StreakMilestoneDialog({ open, onOpenChange, milestone }: StreakMilestoneDialogProps) {
  const info = milestoneMessages[milestone] || {
    title: `${milestone} dní v řadě!`,
    message: "Skvělá práce! Pokračuj v sledování své vnitřní mapy.",
    emoji: "🎉",
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <div className="flex flex-col items-center text-center space-y-4 py-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.5 }}
          >
            <div className="relative">
              <div className="absolute inset-0 animate-ping">
                <Trophy className="h-24 w-24 text-yellow-500/20" />
              </div>
              <Trophy className="h-24 w-24 text-yellow-500 relative z-10" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-2"
          >
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{info.title}</DialogTitle>
              <DialogDescription className="text-base pt-2">
                {info.message}
              </DialogDescription>
            </DialogHeader>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex items-center gap-2 p-4 rounded-lg bg-primary/5 border border-primary/10"
          >
            <Flame className="h-5 w-5 text-orange-500" />
            <span className="text-3xl font-bold text-primary">{milestone}</span>
            <span className="text-sm text-muted-foreground">
              {getCzechDaysText(milestone)}
            </span>
            <Sparkles className="h-5 w-5 text-yellow-500" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="w-full"
          >
            <Button onClick={() => onOpenChange(false)} className="w-full">
              Pokračovat
            </Button>
          </motion.div>

          <p className="text-xs text-muted-foreground">
            Nezapomeň si zaznamenat svůj stav i zítra, aby série pokračovala! 💪
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
